import React from 'react';

type TooltipDirection = 'up' | 'left' | 'down' | 'right' | 'center';

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  direction?: TooltipDirection;
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  direction = 'up',
  className = '',
}) => {
  // Don't render tooltip if content is empty
  if (!content || content.trim() === '') {
    return <>{children}</>;
  }

  const getTooltipClasses = () => {
    const baseClasses =
      'absolute px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none select-none';

    // Light mode: dark background, dark text
    // Dark mode: light background, dark text
    const themeClasses =
      'bg-white dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 shadow-lg';

    // Use higher z-index for left/right tooltips to avoid conflicts with dropdowns
    const zIndexClass =
      direction === 'left' || direction === 'right' ? 'z-50' : 'z-10';

    return `${baseClasses} ${themeClasses} ${zIndexClass} ${className}`;
  };

  const getArrowClasses = () => {
    const baseClasses = 'absolute w-0 h-0';

    switch (direction) {
      case 'up':
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200 dark:border-t-gray-700`;
      case 'down':
        return `${baseClasses} bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-200 dark:border-b-gray-700`;
      case 'left':
        return `${baseClasses} top-1/2 right-full transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-transparent border-l-gray-200 dark:border-l-gray-700`;
      case 'right':
        return `${baseClasses} top-1/2 left-full transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-200 dark:border-r-gray-700`;
      case 'center':
        return ''; // No arrow for center tooltip
      default:
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200 dark:border-t-gray-700`;
    }
  };

  const getTooltipPosition = () => {
    switch (direction) {
      case 'up':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'down':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      case 'center':
        return 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
    }
  };

  return (
    <div className="relative group">
      {children}
      <div
        className={`${getTooltipPosition()} ${getTooltipClasses()}`}
        style={{
          position: 'absolute',
          ...(direction === 'left' && {
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginRight: '8px',
          }),
        }}
      >
        {content}
        {direction !== 'down' && <div className={getArrowClasses()}></div>}
      </div>
    </div>
  );
};

export default Tooltip;
